import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { authService } from '@/services/auth';
import { useAuthStore } from '@/context/authStore';
import Modal from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import { Lock, AlertCircle } from 'lucide-react';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  isForced?: boolean; // Si es true, no se puede cerrar sin cambiar la contraseña
}

export default function ChangePasswordModal({
  isOpen,
  onClose,
  isForced = false,
}: ChangePasswordModalProps) {
  const { updateUser } = useAuthStore();
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [error, setError] = useState<string | null>(null);

  const changePasswordMutation = useMutation({
    mutationFn: authService.changePassword,
    onSuccess: () => {
      updateUser({ must_change_password: false });
      setFormData({ current_password: '', new_password: '', confirm_password: '' });
      setError(null);
      onClose();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Error al cambiar la contraseña');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.new_password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    // Validar requisitos de contraseña segura
    if (!/[A-Z]/.test(formData.new_password)) {
      setError('La contraseña debe contener al menos una mayúscula');
      return;
    }
    if (!/[a-z]/.test(formData.new_password)) {
      setError('La contraseña debe contener al menos una minúscula');
      return;
    }
    if (!/\d/.test(formData.new_password)) {
      setError('La contraseña debe contener al menos un número');
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(formData.new_password)) {
      setError('La contraseña debe contener al menos un caracter especial (!@#$%...)');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError('Las contraseñas no coinciden');
      return;
    }

    changePasswordMutation.mutate({
      current_password: formData.current_password,
      new_password: formData.new_password,
    });
  };

  const handleClose = () => {
    if (!isForced) {
      setFormData({ current_password: '', new_password: '', confirm_password: '' });
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Cambiar Contraseña"
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {isForced && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Cambio de contraseña requerido
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Por seguridad, debes cambiar tu contraseña temporal antes de continuar.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <Input
          label="Contraseña actual"
          type="password"
          value={formData.current_password}
          onChange={(e) =>
            setFormData({ ...formData, current_password: e.target.value })
          }
          placeholder="Ingresa tu contraseña actual"
          required
        />

        <div>
          <Input
            label="Nueva contraseña"
            type="password"
            value={formData.new_password}
            onChange={(e) =>
              setFormData({ ...formData, new_password: e.target.value })
            }
            placeholder="Crea una contraseña segura"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Mínimo 8 caracteres, con mayúscula, minúscula, número y caracter especial
          </p>
        </div>

        <Input
          label="Confirmar nueva contraseña"
          type="password"
          value={formData.confirm_password}
          onChange={(e) =>
            setFormData({ ...formData, confirm_password: e.target.value })
          }
          placeholder="Repite la nueva contraseña"
          required
        />

        <div className="flex justify-end gap-2 pt-4">
          {!isForced && (
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
          )}
          <Button
            type="submit"
            loading={changePasswordMutation.isPending}
          >
            <Lock className="h-4 w-4 mr-2" />
            Cambiar Contraseña
          </Button>
        </div>
      </form>
    </Modal>
  );
}
